import {
  Component,
  ElementRef,
  OnChanges,
  Input,
  ViewChild,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  EventEmitter,
  Output
} from '@angular/core';
import { Channel, MessageResponse } from 'stream-chat';
import * as dayjs from 'dayjs';
import { User, ProductReservation } from '@vestiaire/api-sdk-angular';

import { ChatService } from '@app/+chat/services/chat.service';
import { LocaleService } from '@app/core/i18n';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'vc-chat-content',
  templateUrl: './chat-content.component.html',
  styleUrls: ['./chat-content.component.scss']
})
export class ChatContentComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() channel: Channel;
  @Input() userId: string;
  @Input() receiverInfo: User;
  @Input() isSeller: boolean;
  @Input() senderIsBlocked: boolean;
  @Input() receiverIsBlocked: boolean;
  @Input() receiverIsBanned: boolean;
  @Input() senderIsBanned: boolean;
  @Input() isReserved: boolean;
  @Input() reservation: ProductReservation;

  @Output() senderHasBeenBlocked: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('chatWrapper', { read: ElementRef, static: true }) chatWrapper: ElementRef;
  @ViewChild('chatContent', { read: ElementRef, static: true }) chatContent: ElementRef;

  ngUnsubscribe = new Subject();
  currentMessage = '';
  translated = false;
  limitCharacter = 500;
  messages: MessageResponse[] = [];
  msgBeenRead = false;
  defaultMessage = {
    type: '',
    html: ''
  };
  tempStatus = {
    NO_NETWORK: 'no-network',
    SENDING: 'sending'
  };
  timeout = null;
  isAbusive = false;
  canFetchMore = true;
  observer: IntersectionObserver;
  locale = '';
  isLoading = false;
  channelId = '';

  constructor(private chatService: ChatService, private localeService: LocaleService, private elementRef: ElementRef) {
    this.locale = this.localeService.getLanguage();
    this.toggleTranslate();
    this.chatService.sendingMessage.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: text => this.sendMessage(text)
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const { channel, newMsg } = changes;
    if (channel && channel.currentValue) {
      this.initData();
    }
  }

  ngAfterViewInit() {
    this.observeScrollTop();
  }

  ngOnDestroy() {
    this.observer.disconnect();
    this.channel.off(this.channelHandler);
    this.chatService.streamChat.off(this.chatHandler);
    this.ngUnsubscribe.next();
  }

  initData() {
    this.chatService.markAsRead(this.channel);
    this.lastMsgRead();
    this.addChannelHandler();
    this.scrollToBottom();
    // use channel cid instead of channel id for better performance
    this.channelId = this.channel.cid.substring(this.channel.cid.indexOf(':') + 1);
  }

  lastMsgRead() {
    const channelState_read = this.channel.state.read;
    this.msgBeenRead = Object.keys(channelState_read).some(
      id => this.userId && id !== this.userId && channelState_read[id]['unread_messages'] === 0
    );
  }

  getPreviousMessages() {
    const lastMessageId = this.messages.length > 0 ? this.messages[0].id : null;
    this.isLoading = true;
    this.chatService
      .getMessages(this.channel, lastMessageId)
      .then(messages => {
        if (messages.length > 0) {
          this.messages = [...messages, ...this.messages];
          if (lastMessageId === null) {
            this.scrollToBottom();
          } else {
            const element = this.elementRef.nativeElement.querySelectorAll('.msgChat')[messages.length];
            if (element) {
              this.chatWrapper.nativeElement.scrollTop = element.offsetTop;
            }
          }
        } else {
          this.canFetchMore = false;
        }
      })
      .catch(err => console.error('[CHAT] previous messages', err))
      .finally(() => (this.isLoading = false));
  }

  sendMessage(text: string) {
    this.msgBeenRead = false;
    this.messages = [...this.messages, this.formatMessage(text, this.tempStatus.SENDING)];
    const tempIndex = this.messages.length - 1;
    this.chatService
      .sendMessage(this.channel, text)
      .then(_ => {})
      .catch(_ => {
        this.messages = [...this.messages, this.formatMessage(text, this.tempStatus.NO_NETWORK)];
      })
      .finally(() => {
        this.messages = [...this.messages.slice(0, tempIndex), ...this.messages.slice(tempIndex + 1)];
      });
  }

  addChannelHandler() {
    this.channel.on(this.channelHandler);
    this.chatService.streamChat.on(this.chatHandler);
  }

  channelHandler = event => {
    switch (event.type) {
      case 'message.new':
        this.chatService.translateMessage(event.message.id, this.locale).then(message => {
          message.status = 'received';
          this.messages = [...this.messages, message];
          this.scrollToBottom();
          if (message.user.id !== this.userId) {
            this.chatService.markAsRead(this.channel);
          }
        });
        break;
      case 'message.read':
        if (event.user && event.user.id !== this.userId) {
          this.msgBeenRead = true;
        }
        break;
      default:
        break;
    }
  }

  chatHandler = event => {
    switch (event.type) {
      case 'user.unbanned':
        if (event.channel_id === this.channelId) {
          this.senderHasBeenBlocked.emit(false);
        }
        break;
      case 'user.banned':
        if (event.channel_id === this.channelId) {
          this.senderHasBeenBlocked.emit(true);
        }
        break;
      default:
        break;
    }
  }

  toggleTranslate() {
    this.translated = true;
    if (this.translated) {
      Promise.all(
        this.messages.map(msg => {
          // Call api if msg is not already translated
          return msg.i18n && msg.i18n[this.locale + '_text']
            ? Promise.resolve(msg)
            : this.chatService.translateMessage(msg.id, this.locale);
        })
      )
        .then(res => (this.messages = [...res]))
        .catch(_ => {});
    }
  }

  onSubmit() {
    if (this.currentMessage.length < this.limitCharacter) {
      this.sendMessage(this.currentMessage);
      this.currentMessage = '';
    }
  }

  observeScrollTop() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0] && entries[0].isIntersecting && this.channel && this.canFetchMore) {
          this.getPreviousMessages();
        }
      },
      {
        root: this.chatWrapper.nativeElement
      }
    );
    this.observer.observe(this.chatContent.nativeElement);
  }

  scrollToBottom() {
    setTimeout(() => {
      try {
        this.chatWrapper.nativeElement.scrollTop = this.chatWrapper.nativeElement.scrollHeight + 100;
      } catch (error) {}
    }, 0);
  }

  // concern sending and failed status message
  formatMessage(text: string, status: string) {
    return {
      ...this.defaultMessage,
      text,
      status,
      id: dayjs().format(),
      created_at: dayjs().format(),
      updated_at: dayjs().format(),
      user: this.chatService.streamChat.user
    };
  }

  retryToSend(id: number) {
    const index = this.messages.findIndex(e => e.id === id);
    if (this.messages[index] && this.messages[index].status === this.tempStatus.NO_NETWORK) {
      const { text } = this.messages[index];
      this.messages = [...this.messages.slice(0, index), ...this.messages.slice(index + 1)];
      this.sendMessage(text);
    }
  }
}
